import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import Routes from '../../routes';
import moment from 'moment';
import {
    BreadCrumbs,
    PrimaryButton,
    SecondaryButton,
    CommonModal,
    CircularLoading,
    ExitConfirmationModal,
    Error404,
    LocationPermissionModal,
} from '../Common';
import {
    getAdditionalRequiredFields,
    initialWorkOrderState,
    requiredFields,
    workOrderLabelStyle,
    gridContainerProps,
    getRequiredFields,
} from './utils';
import Form from './form';
import Rocket from '../../Icons/img-rocket_launch.svg';
import { Grey2 } from '../../color_utils';
import { trakrefFontFamily, postRequestHeaders, formatDateWithZ, readJsonSafe, getResponseErrorMessage } from '../../utils';
import { TranslateW } from '../../Utils/Languages/Translate';
import { TranslateArray } from '../../Utils/Languages/TranslateDropdown';
import {
    Grid,
    Typography,
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Skeleton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import UserInfo from "../../UserInfo";
import LoadingButton from '@mui/lab/LoadingButton';
import Enums from '../../enums';
import { useIntl } from 'react-intl';
import {
    useNavigate,
    useBreadCrumbs,
    useFormDirty
} from '../../hooks';
import { setFiltersPushUser } from "../Common/DataGrid/utils";
import { red } from '@mui/material/colors';
import CodeConvert from '../../Utils/DropDownConvert';

const AddWorkOrder = ({
    history = {},
    match: {
        params: {
            id = '',
        } = {}
    } = {},
    location: {
        state: {
            from = 'Work Orders',
        } = {}
    } = {},
}) => {
    const intl = useIntl();
    const [error404Loading, setError404Loading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [originalData, setOriginalData] = useState({});
    const [locations, setLocations] = useState([]);
    const [allLocations, setAllLocations] = useState([]);
    const [priorities, setPriorities] = useState([]);
    const [serviceCompanies, setServiceCompanies] = useState([]);
    const [types, setTypes] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [statusReasonOptions, setStatusReasonOptions] = useState(null);
    const [completedPopup, setCompletedPopup] = useState(false);
    const [newCreatedID, setNewCreatedID] = useState(null);
    const [proposalRequired, setProposalRequired] = useState(null);
    const [exitModal, setExitModal] = useState(false);

    // Use My Location state
    const [useMyLocation, setUseMyLocation] = useState(false);
    const [locationRadius, setLocationRadius] = useState(100); // default 100 miles
    const [userCoordinates, setUserCoordinates] = useState(null);
    const [locationPermissionModal, setLocationPermissionModal] = useState(false);
    const [nearbyLocationsMessage, setNearbyLocationsMessage] = useState('');
    const [isLoadingLocations, setIsLoadingLocations] = useState(false);
    const debounceTimerRef = useRef(null);

    const { initializeData, isDirty } = useFormDirty();
    const navigate = useNavigate({
        pageName: 'AddWorkOrder'
    });
    const breadCrumbs = useBreadCrumbs({
        path: { id: 'AddWorkOrder', text: id ? 'Edit' : 'Create Work Order', link: history.location.pathname, shouldBeReplaced: !id }
    });

    const [state, setAllState] = useState({
        workOrders: [{ ...initialWorkOrderState, requiredFields: getRequiredFields() }],
        loading: false,
        currentWorkOrderIndex: 0,
    });
    const [errorState, setErrorState] = useState({
        workOrders: [{ id: null, ...initialWorkOrderState }]
    });

    const setState = new_state_obj => setAllState(prev => ({ ...prev, ...new_state_obj }));
    const setErrors = new_error_obj => setErrorState(prev => ({ ...prev, ...new_error_obj }));

    useEffect(() => {
        fetchData();
    }, []);

    const closePage = () => {
        const url = isEditing ? `/WorkOrder/${id}` : "/WorkOrders";
        navigate.goBack(url, from);
    };

    const fetchData = async () => {
        const response = await fetch(Routes.WorkOrder.AddWorkOrderDropdowns());
        const data = await response.json();

        setLocations(data.locations);
        setAllLocations(data.locations);
        setServiceCompanies(data.servicers);
        setTypes(TranslateArray(intl, data.types, "WorkOrderType"));
        setPriorities(TranslateArray(intl, data.priorities, "TroubleTicketPriority"));

        let statusFilter = data.workOrderStatusReason.filter(obj => obj.serviceActivityOrder !== 0);
        statusFilter = CodeConvert.Convert(statusFilter);
        setStatusReasonOptions(statusFilter);

        if (id) {
            setIsEditing(true);
            const response = await fetch(Routes.WorkOrder.DetailsForEditing(parseInt(id)));
            const workOrder = await response.json();
            // add Proposal Required option to dropdown list

            if (workOrder.status === 404) {
                setError404Loading(true);
                setLoading(false);
                return;
            }

            if (workOrder.workOrderStatusReasonId === 48) {
                const proposalRequiredOption = data.workOrderStatusReason.find(i => i.id === 48);
                statusFilter = CodeConvert.Convert([proposalRequiredOption, ...statusFilter]);
                setStatusReasonOptions(statusFilter);
            }
            setEditingData(workOrder, data.locations);
            initializeData(setEditingData(workOrder, data.locations, true));
        }

        setLoading(false);
    };

    const setEditingData = (data, locationOptions, returnFormattedData = false) => {
        console.log('workOrder data to be edited', data);

        const location = locationOptions.find(l => l.id === data.assetLocationId);
        const dueDateFormat = formatDateWithZ(data.dueDate);
        const schedueDateFormat = formatDateWithZ(data.scheduledDate);
        const locationName = (location && 'name' in location) && location.name;
        const displayName = `Work Order #${data.workOrderNumber} • ${locationName} • ${schedueDateFormat}`;
        const updateObject = {
            type: data.workOrderTypeId,
            workOrderNumber: data.workOrderNumber,
            statusReason: data.workOrderStatusReasonId,
            dueDate: dueDateFormat,
            priorityId: data.troubleTicketPriorityId,
            purchaseOrderNumber: data.purchaseOrderNumber,
            workScheduled: data.requestDetails,
            priorityDescription: data.requestDetails,
            locationId: data.assetLocationId,
            lowDollarLimit: parseInt(data.lowDollarLimit),
            highDollarLimit: parseInt(data.highDollarLimit),
            serviceCompanyId: data.servicerInstanceId,
            laborCost: parseInt(data.laborCost),
            materialCost: parseInt(data.materialCost),
            otherCost: parseInt(data.otherCost),
            scheduledDate: schedueDateFormat,
            displayName,
            proposalRequired: id > 0 && data.workOrderStatusReasonId === 48,
            requiredFields
        };

        if (returnFormattedData) return updateObject;

        return setState({ workOrders: [{ ...updateObject }] });
    };

    const validate = () => {
        setErrors({});
        const errors = { workOrders: [] };
        let errorExists = false;

        state.workOrders.forEach((workOrder, index) => {
            workOrder.requiredFields.forEach((required_field) => {
                const fieldValue = workOrder[required_field];
                if (!fieldValue ||  (required_field === 'scheduledDate' && isNaN(new Date(fieldValue).getTime()))) {
                    errorExists = true;
                    if (errors.workOrders[index]) {
                        return errors.workOrders[index][required_field] = true;
                    }
                    return errors.workOrders[index] = { [required_field]: true };
                }
            });
        });

        console.log('errors before saving', errors);
        setErrors(errors);
        return errorExists;
    };

    const handleDuplicateWorkOrderNumber = (index) => {
        const errors = { workOrders: state.workOrders.map(() => ({})) };
        errors.workOrders[index]["workOrderNumber"] = "This Work Order number is already in use";
        return setErrors(errors);
    };

    const handleSuccess = async (response, withCreateService) => {
        const [workOrder] = response;

        // Duplicate Work Order number detected
        // workOrder is undefined
        if (!workOrder) {
            return null;
        }

        // Only creating 1 workOrder - show success modal
        if (response.length === 1) {
            const { id, updateSuccessful = false } = workOrder;
            setNewCreatedID(id);

            if (updateSuccessful) {
                closePage();
                return toast.success('Work Order sucessfully updated.', { duration: 6000 });
            }

            if (withCreateService) {
                const from_route = `/${from.replace(" ", "")}`;
                return navigate.push(
                    `/CreateServiceEvent/${id}`,
                    null,
                    {
                        root: from_route
                    }
                );
            }

            return setCompletedPopup(true);
        }

        // Creating multiple workOrders - push to work order page with filter applied
        const today = moment().format('L');

        toast.success('Work Orders successfully created.', { duration: 6000 });
        
        return setFiltersPushUser({
            destinationTableUrl: Routes.WorkOrder.WorkOrderTable(),
            destinationPath: "/WorkOrders",
            items: [{ field: "createdOn", value: `${today}-${today}` }],
            navigate
        });
    };

    const convertToAcceptableDate = (date) => {

        if (date == null || date == undefined) {
            return null;
        }

        const OriginalDate = new Date(date);

        let year = OriginalDate.getFullYear();
        if (year < 2000) {
            year += 2000;
        }
        const month = OriginalDate.getMonth() + 1
        const day = OriginalDate.getDate();

        const DateTime = new Date(`${year}-${month}-${day}`);

        return DateTime;
    };

    const checkLocalWoNumbers = () => {
        let isValid = true;
        state.workOrders.forEach((wo, index) => (
            state.workOrders
                .slice(index + 1)
                .map(wo => wo.workOrderNumber)
                .forEach((woNumber, i) => {
                    if (wo.workOrderNumber === woNumber && woNumber !== "") {
                        isValid = false;
                        handleDuplicateWorkOrderNumber(i);
                    }
                })
        ));
        return isValid;
    };

    const postWorkOrder = async (workOrder, index) => (
        validateWorkOrderNumber(workOrder)
            .then(async (workOrderNumberExists) => {
                const allLocalWoNumbersValid = checkLocalWoNumbers();
                if (workOrderNumberExists || !allLocalWoNumbersValid) {
                    return handleDuplicateWorkOrderNumber(index);
                }
                return fetch(Routes.WorkOrder.AddWorkOrder(), {
                    method: 'POST',
                    headers: { ...postRequestHeaders },
                    body: JSON.stringify(workOrder)
                }).then(async (response) => {
                    // AB#17142: a bare 403 (empty body) used to surface as a raw JSON parse error toast
                    const data = await readJsonSafe(response);
                    if (!response.ok || !data?.id) {
                        const createError = new Error(getResponseErrorMessage(response, data, 'Failed to create the work order.'));
                        createError.name = "Error Creating Work Order";
                        throw createError;
                    }
                    return {
                        id: data.id,
                        response
                    };
                })
            })
    );

    const validateWorkOrderNumber = async (workOrder) => {
        if (!UserInfo.isWorkOrderNumberRequired() && !workOrder.WorkOrderNumber) {
            return false;
        }

        const encodedWorkOrderNumber = encodeURIComponent(workOrder.WorkOrderNumber);
        const URL = Routes.WorkOrder.ValidateWorkOrderNumber(encodedWorkOrderNumber);
        const workOrderNumberExists = await fetch(URL).then(res => res.json());
        return workOrderNumberExists;
    };

    const updateWorkOrder = async (workOrder) => (
        fetch(Routes.WorkOrder.UpdateWorkOrder(), {
            method: 'POST',
            headers: { ...postRequestHeaders },
            body: JSON.stringify(workOrder)
        })
            .then(async (response) => {
                const res = await readJsonSafe(response);

                // Successful Update - WorkOrderId returned
                if (response.ok && res?.id) {
                    return {
                        id: res.id,
                        response,
                        updateSuccessful: true
                    };
                }

                // Error updating - throw error (AB#17142: also covers an empty 403 body)
                const updateError = new Error(getResponseErrorMessage(response, res, 'Failed to update the work order.'));
                updateError.name = "Error Updating Work Order";
                throw updateError;
            })
    );

    const formatWorkOrderData = (workOrder, instance) => {
        const workOrderData = {
            TypeId: workOrder.type,
            InstanceId: instance.instanceId,
            WorkOrderNumber: workOrder.workOrderNumber,
            PurchaseOrderNumber: workOrder.purchaseOrderNumber,
            LocationId: workOrder.locationId,
            ServiceCompanyId: workOrder.serviceCompanyId,
            ScheduledDate: convertToAcceptableDate(workOrder.scheduledDate),
            RequestDetails: workOrder.type === Enums.WorkOrderTypes.ScheduledMaintenance
                ? workOrder.workScheduled
                : workOrder.priorityDescription,
            DueDate: convertToAcceptableDate(workOrder.dueDate),
            PriorityId: workOrder.priorityId,
            LowDollarLimit: workOrder.lowDollarLimit,
            HighDollarLimit: workOrder.highDollarLimit,
            ProposalRequired: workOrder.proposalRequired,
            CreateUserId: UserInfo.userId,
            UpdateUserId: UserInfo.userId
        };
        if (isEditing) {
            const localLaborCost = parseFloat(workOrder.laborCost) || 0;
            const localMaterialCost = parseFloat(workOrder.materialCost) || 0;
            const localOtherCost = parseFloat(workOrder.otherCost) || 0;
            workOrderData.ID = id;
            workOrderData.WorkOrderStatusReasonId = workOrder.statusReason;
            workOrderData.LaborCost = localLaborCost;
            workOrderData.MaterialCost = localMaterialCost;
            workOrderData.OtherCost = localOtherCost;
            workOrderData.TotalCost = parseFloat(localLaborCost + localMaterialCost + localOtherCost) || 0;
            workOrderData.UpdateUserId = UserInfo.userId;
        }
        return workOrderData;
    };

    const submit = async (withCreateService = false) => {
        setState({ loading: true });
        const errorsExist = validate();
        console.log('errorsExist', errorsExist);

        if (!errorsExist) {
            const { workOrders } = state;

            const workOrdersPromise = workOrders.map((workOrder, index) => {
                const instance = locations.find(loc => loc.id === workOrder.locationId);
                const workOrderData = formatWorkOrderData(workOrder, instance);

                if (isEditing) {
                    return updateWorkOrder(workOrderData);
                }

                return postWorkOrder(workOrderData, index);
            });

            await Promise
                .all(workOrdersPromise)
                .then(res => handleSuccess(res, withCreateService))
                .catch((err) => {
                    console.log(err);
                    toast.error(err.message, { duration: 6000 });
                });
        }
        return setState({ loading: false });
    };

    const renderBreadCrumbs = () => {
        if (loading) {
            return (
                <Skeleton variant="text" sx={{ fontSize: '1rem', width: '10%' }} />
            );
        }

        return (
            <BreadCrumbs
                path={breadCrumbs.getSortedBreadCrumbs()}
            />
        );
    };

    const getEditedWorkOrder = (index) => {
        const workOrdersClone = state.workOrders.map(wo => ({ ...wo }));
        const modifiedWorkOrder = workOrdersClone[index];
        return { modifiedWorkOrder, workOrders: workOrdersClone };
    };

    const handleWorkOrderFieldData = (index, field, value, setAdditionalRequiredFields = false) => {
        let { modifiedWorkOrder } = getEditedWorkOrder(index);
        const { workOrders } = getEditedWorkOrder(index);
        modifiedWorkOrder = { ...modifiedWorkOrder, [field]: value };

        if (value && setAdditionalRequiredFields) {
            const additionalRequiredFields = getAdditionalRequiredFields(value);
            modifiedWorkOrder.requiredFields = [...additionalRequiredFields];
        }

        workOrders[index] = modifiedWorkOrder;
        return setState({ workOrders });
    };

    const getDisplayNames = () => {
        return state.workOrders.map(wo => {
            const location = locations.find(l => l.id === wo.locationId);
            const formattedScheduleDate = moment(wo.scheduledDate).format('MM/DD/YYYY');
            const displayName = `Work Order #${wo.workOrderNumber} • ${location.name} • ${formattedScheduleDate}`;
            return { ...wo, displayName, open: false };
        });
    };

    const addWorkOrder = async () => {
        const errorsExist = validate();
        if (errorsExist) {
            return null;
        }
        const currentWorkOrderNumber = state.workOrders[state.currentWorkOrderIndex].workOrderNumber;
        const duplicateWorkOrderNumber = await validateWorkOrderNumber({ WorkOrderNumber: currentWorkOrderNumber });
        const allLocalWoNumbersValid = checkLocalWoNumbers();
        if (duplicateWorkOrderNumber || !allLocalWoNumbersValid) {
            return handleDuplicateWorkOrderNumber(state.currentWorkOrderIndex);
        }
        const newWorkOrder = { ...initialWorkOrderState, requiredFields: getRequiredFields() };
        const updatedWorkOrders = getDisplayNames();
        return setState({
            workOrders: [...updatedWorkOrders, newWorkOrder],
            currentWorkOrderIndex: state.currentWorkOrderIndex + 1,
        });
    };

    const deleteWorkOrder = (e, index) => {
        e.stopPropagation();

        if (state.workOrders.length === 1) {
            return;
        }

        const filteredWorkOrders = state.workOrders.filter((_, i) => i !== index);
        setState({
            workOrders: filteredWorkOrders,
            currentWorkOrderIndex: state.currentWorkOrderIndex - 1
        })

        return toast.success('Work Order successfully deleted.', { duration: 6000 });
    };

    const goBack = () => {
        setCompletedPopup(false);
        navigate.goBack('/WorkOrders');
    };

    const handleExit = () => {
        const [firstWorkOrder] = state.workOrders;
        const anyDataExists = firstWorkOrder.requiredFields
            .map(rf => Boolean(firstWorkOrder[rf]))
            .includes(true);

        // Create - only tracking required fields
        if (!isEditing && anyDataExists) {
            return setExitModal(true);
        }
        
        // Edit
        if (isEditing && isDirty(firstWorkOrder)) {
            return setExitModal(true);
        }

        return closePage();
    };

    const fetchNearbyLocations = async (lat, lng, radius) => {
        setIsLoadingLocations(true);
        try {
            const response = await fetch(Routes.WorkOrder.AddWorkOrderDropdowns({
                userLat: lat,
                userLng: lng,
                radiusMiles: radius
            }));
            const data = await response.json();

            if (data.locations.length === 0) {
                setNearbyLocationsMessage('No nearby locations found for this distance. Showing all locations instead.');
                // Fetch all locations when no nearby found
                const allLocationsResponse = await fetch(Routes.WorkOrder.AddWorkOrderDropdowns());
                const allLocationsData = await allLocationsResponse.json();
                setLocations(allLocationsData.locations);
            } else {
                setNearbyLocationsMessage('Nearby locations listed.');
                setLocations(data.locations);
            }
        } catch (error) {
            console.error('Error fetching nearby locations:', error);
            setNearbyLocationsMessage('Error fetching nearby locations');
        } finally {
            setIsLoadingLocations(false);
        }
    };

    // Handle "Use my Location" toggle
    const handleUseMyLocationChange = (checked) => {
        setUseMyLocation(checked);

        if (checked && !userCoordinates) {
            // Request browser geolocation
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        const coords = {
                            lat: position.coords.latitude,
                            lng: position.coords.longitude
                        };
                        setUserCoordinates(coords);
                        fetchNearbyLocations(coords.lat, coords.lng, locationRadius);
                    },
                    (error) => {
                        setUseMyLocation(false);
                        setLocationPermissionModal(true);
                    }
                );
            } else {
                setUseMyLocation(false);
                setLocationPermissionModal(true);
            }
        } else if (checked && userCoordinates) {
            fetchNearbyLocations(userCoordinates.lat, userCoordinates.lng, locationRadius);
        } else if (!checked) {
            // Reset to all locations
            setNearbyLocationsMessage('');
            setLocations(allLocations);
        }
    };

    const handleRadiusChange = (value) => {
        setLocationRadius(value);

        // Clear any existing timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        if (userCoordinates) {
            debounceTimerRef.current = setTimeout(() => {
                fetchNearbyLocations(userCoordinates.lat, userCoordinates.lng, value);
            }, 500);
        }
    };

    const Loading = () => (
        <>
            <Grid {...gridContainerProps} mt={2}>
                <Grid item>
                    <Skeleton variant="circular" width={40} height={40} />
                </Grid>
                <Grid item style={{ marginLeft: '10px' }}>
                    <Skeleton variant="text" sx={{ fontSize: '1rem' }} width={130} height={40} />
                </Grid>
                <Grid item style={{ marginLeft: 'auto' }}>
                    <Skeleton
                        variant='rectangular'
                        width={150}
                        height={35}
                    />
                </Grid>
            </Grid>

            <Grid container sx={{ ml: { xs: 0, md: 4 }, mr: { xs: 0 }, maxWidth: { xs: '100%', sm: '100%', md: '65%' } }} spacing={{ xs: 1, md: 2 }}>
                {state.workOrders.map((workOrder, index) => (
                    <Grid item xs={12}>
                        <Accordion
                            variant='outlined'
                            expanded={workOrder.open || isEditing}
                            onChange={(_, isExpanded) => handleWorkOrderFieldData(index, 'open', isExpanded)}
                            TransitionProps={{ unmountOnExit: true }}
                        >
                            <AccordionSummary>
                                <Grid container direction='row'>
                                    <Grid item sx={10}>
                                        <Skeleton width={455} height={33} />
                                    </Grid>
                                </Grid>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container spacing={4}>
                                    {new Array(11).fill(null).map(() => (
                                        <Grid item xs={6} mb={1}>
                                            <Skeleton
                                                variant='rectangular'
                                                height={50}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    </Grid>
                ))}
            </Grid>
        </>
    );

    const locationIds = locations.map(x => x.id);
    const workOrderLocationIds = state.workOrders.map(x => x.locationId);
    const canEdit = locationIds.some(id => workOrderLocationIds.includes(id)) && !UserInfo.isReadOnly() && (
        (UserInfo.isSystemOwner() && (UserInfo.isSuperTenantAdmin() || UserInfo.isTenantAdministrator() || UserInfo.isRegionalAdministrator() || UserInfo.isTechnicianManager() || UserInfo.isTechnician())) ||
        (UserInfo.isServiceProvider() && (UserInfo.isTenantAdministrator() || UserInfo.isTechnician())) ||
        (UserInfo.isSystemAdministrator())
    );
    
    if (id && !loading && (!canEdit || error404Loading)) {
        return <Error404/>
    }

    return (
        <>
            <Grid sx={{ display: { xs: 'none', lg: 'block' } }}>
                {renderBreadCrumbs()}
            </Grid>

            {loading ? (
                <Loading />
            ) : (
                <>
                    <Grid {...gridContainerProps}  spacing={1}>
                        <Grid item>
                            <IconButton aria-label="close" onClick={handleExit}>
                                <CloseIcon />
                            </IconButton>
                        </Grid>
                        <Grid item>
                            <Typography variant="h5" style={{ color: '#1A173B' }} id="pageHeading">
                                {isEditing ? 'Edit' : 'Create'} Work Order
                            </Typography>
                        </Grid>
                        {isEditing ? (
                            <>
                                <Grid item style={{ marginLeft: 'auto' }}>
                                    <PrimaryButton
                                        buttonText={TranslateW("workOrderUpdate.update-workorder")}
                                        onClick={submit}
                                    />
                                </Grid>
                            </>
                        ) : (
                            <>
                                <Grid item sx={{ ml: 'auto', display: { xs: 'none', md: 'flex' }, gap: 1 }}>
                                    {state.loading ? (
                                        <LoadingButton loading variant="outlined">
                                            <span>Submit</span>
                                        </LoadingButton>
                                    ) : (
                                        <SecondaryButton
                                            id="createWorkOrderAddServiceEvent"
                                            buttonText={TranslateW("createWorkOrderAddServiceEvent")}
                                            onClick={() => submit(true)}
                                            disabled={state.workOrders.length > 1}
                                        />
                                    )}
                                    {state.loading ? (
                                        <LoadingButton loading variant="outlined">
                                            <span>Submit</span>
                                        </LoadingButton>
                                    ) : (
                                        <PrimaryButton
                                            id="create_Button"
                                            buttonText={TranslateW("create")}
                                            onClick={() => submit(false)}
                                            disabled={proposalRequired === true}
                                        />
                                    )}
                                </Grid>
                            </>
                        )}
                    </Grid>

                    <Grid container sx={{ mb: { xs: 1, lg: 4 }, ml: { xs: 0, lg: 4}, mr: { xs: 0 }, maxWidth: { xs: '100%', lg: '65%' }, p: { xs: 1, sm: 0}  }} spacing={{ xs: 1, md: 2}}>
                        {state.workOrders.map((workOrder, index) => (
                            <Grid item xs={12} sx={{ pl: { xs: '0 !important', md: 1 } }}>
                                <Accordion
                                    variant='outlined'
                                    expanded={workOrder.open || isEditing}
                                    onChange={(_, isExpanded) => handleWorkOrderFieldData(index, 'open', isExpanded)}
                                    TransitionProps={{ unmountOnExit: true }}
                                >
                                    <AccordionSummary
                                        id="expandOrCollapseWorkOrder"
                                        expandIcon={isEditing ? <></> : <ExpandMoreIcon />}
                                        sx={{
                                            '&.MuiAccordionSummary-root': {
                                                cursor: isEditing ? 'default !important' : 'pointer',
                                                minHeight: { xs: 'auto', md: 48 },
                                            },
                                            '& .MuiAccordionSummary-content': {
                                                margin: { xs: '8px 0px', md: '12px 0' }
                                            }
                                        }}
                                    >
                                        <Grid container direction='row'>
                                            <Grid item sx={10}>
                                                <Typography
                                                    sx={workOrderLabelStyle}
                                                    id="workOrderDisplayName"
                                                >
                                                    {workOrder.displayName || 'New Work Order'}
                                                    {state.loading && <CircularLoading size={15} marginLeft={1} />}
                                                </Typography>
                                            </Grid>
                                            {!isEditing && (
                                                <Grid item sx={{ marginLeft: 'auto', display: { xs: 'none', lg: 'block' } }}>
                                                    <IconButton
                                                        id="deleteWorkOrder"
                                                        onClick={e => deleteWorkOrder(e, index)}
                                                        disableRipple={state.workOrders.length === 1}
                                                        sx={state.workOrders.length === 1 && {
                                                            color: "#c7c7c7"
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Form
                                            index={index}
                                            workOrder={workOrder}
                                            errors={errorState.workOrders[index] || {}}
                                            handleWorkOrderFieldData={handleWorkOrderFieldData}
                                            isEditing={isEditing}
                                            selectOptions={{
                                                types,
                                                statusReasonOptions,
                                                priorities,
                                                locations,
                                                serviceCompanies,
                                            }}
                                            useMyLocation={useMyLocation}
                                            locationRadius={locationRadius}
                                            onUseMyLocationChange={handleUseMyLocationChange}
                                            onRadiusChange={handleRadiusChange}
                                            nearbyLocationsMessage={nearbyLocationsMessage}
                                            isLoadingLocations={isLoadingLocations}
                                        />
                                    </AccordionDetails>
                                </Accordion>
                            </Grid>
                        ))}
                    </Grid>

                    {!isEditing && !UserInfo.isTechnician() && (
                        <Grid container item justifyContent='flex-end' sx={{ maxWidth: { xs: '100%', lg: 'calc(65% + 32px)' }, display: { xs: 'none', lg: 'flex' }, mb: { md: 2 } }}>
                            {/* Desktop button */}
                            <Grid item>
                                <SecondaryButton
                                    id="createMultipleAtOnce"
                                    buttonText='Create Multiple At Once'
                                    onClick={addWorkOrder}
                                />
                            </Grid>
                        </Grid>
                    )}

                    {/* Mobile buttons at bottom */}
                    {!isEditing && (
                        <Grid container direction='column' spacing={1} sx={{ display: { xs: 'flex', md: 'none' }, p: 1, mb: 4 }}>
                            <Grid item>
                                {state.loading ? (
                                    <LoadingButton loading variant="outlined" fullWidth>
                                        <span>Submit</span>
                                    </LoadingButton>
                                ) : (
                                    <PrimaryButton
                                        id="create_Button_Mobile"
                                        buttonText={TranslateW("create")}
                                        onClick={() => submit(false)}
                                        disabled={proposalRequired === true}
                                        width={100}
                                    />
                                )}
                            </Grid>
                            <Grid item>
                                {state.loading ? (
                                    <LoadingButton loading variant="outlined" fullWidth>
                                        <span>Submit</span>
                                    </LoadingButton>
                                ) : (
                                    <SecondaryButton
                                        id="createWorkOrderAddServiceEvent_Mobile"
                                        buttonText={TranslateW("createWorkOrderAddServiceEvent")}
                                        onClick={() => submit(true)}
                                        disabled={state.workOrders.length > 1}
                                        width={100}
                                    />
                                )}
                            </Grid>
                        </Grid>
                    )}
                </>
            )}

            <CommonModal
                open={completedPopup}
                close={() => goBack()}
            >
                <Grid container>
                    <Grid item xs={12} align='center'>
                        <img
                            style={{ paddingTop: '1%', paddingBottom: '5%' }}
                            src={Rocket}
                            width="300px"
                            height="300px"
                            alt='Success'
                        />
                    </Grid>
                    <Grid item xs={12} align='center'>
                        <Typography
                            variant="h5"
                            sx={{ '&.MuiTypography-root': { fontFamily: trakrefFontFamily } }}
                            style={{ marginBottom: '6%', color: Grey2 }}
                            align='center'
                        >
                            Work Order successfully created.
                        </Typography>
                    </Grid>
                    <Grid item xs={12} align='center' marginBottom={"3%"}>
                        <PrimaryButton
                            width={70}
                            useLargeButton
                            buttonText='VIEW WORK ORDER'
                            onClick={() => navigate.push(`/WorkOrder/${newCreatedID}`, {replace: true})}
                        />
                    </Grid>
                    <Grid item xs={12} align='center' marginBottom={"3%"}>
                        <SecondaryButton
                            useLargeButton
                            width={70}
                            onClick={() => window.location.reload(false)}
                            buttonText='CREATE NEW WORK ORDER'
                        />
                    </Grid>
                    {proposalRequired && (
                        <Grid item xs={12} align='center' marginBottom={"3%"}>
                            <SecondaryButton
                                useLargeButton
                                width={70}
                                onClick={() => navigate.push(`/WorkOrder/${newCreatedID}`)}
                                buttonText='Create Proposal'
                            />
                        </Grid>
                    )}
                </Grid>
            </CommonModal>

            <ExitConfirmationModal
                open={exitModal}
                close={() => setExitModal(false)}
                leaveOnClick={closePage}
                stayOnClick={() => setExitModal(false)}
                isEditing={isEditing}
            />

            <LocationPermissionModal
                open={locationPermissionModal}
                close={() => setLocationPermissionModal(false)}
            />
        </>
    )
};

export default AddWorkOrder;
